import React, { useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { Toasts, useModalContext } from '@fourthwall/components'
import type { ApiError } from '@fourthwall/services/dist/types'
import { routing } from '@utils/routing'

import { useDnsQuery } from '../../../../../../hooks/useDnsQuery'
import { useEntri } from '../../hooks/useEntri'
import { ConfirmDomainConnectModal } from '../../modules/ConfirmDomainConnectModal/ConfirmDomainConnectModal'
import { useCreateDomainMutation } from '../../queries/useCreateDomainMutation'
import type { FormValues } from './NewDomain.types'
import { NewDomainView } from './NewDomainView'
import { useCheckDomain } from './hooks/useCheckDomain'
import { normalizeDomain } from './utils/normalizeDomain'

export const NewDomain = () => {
  const navigate = useNavigate()
  const toastId = useRef<string | null>(null)
  const { open } = useModalContext()
  const { dnsQuery, invalidateDnsQuery } = useDnsQuery()
  const { createDomainMutation } = useCreateDomainMutation()
  const { isLoading, setIsLoading, setIsQueryEnabled } = useCheckDomain({
    onConnect: () => {
      toastId.current && Toasts.dismiss(toastId.current)
    },
  })
  const { isEntriLoaded } = useEntri()
  const dnsList = dnsQuery.data?.dnsList

  const handleCreateError = (domain: string, error: ApiError) => {
    switch (error?.response?.data?.code) {
      case 'DNS_FOR_SHOP_REGISTERED':
        navigate(routing.settings.domain.self)

        return 'DNS domain for shop is already registered'
      case 'DNS_DOMAIN_NAME_REGISTERED':
        return `${domain} is already connected to a different Fourthwall site. If you’d like to connect it to this account, you must first remove the current connection`
      case 'SUBDOMAIN_OF_BASE_DOMAIN':
        return 'Dns domain is part of base domain'
      default:
        return error?.message
    }
  }

  const handleSubmitConfirm = (values: FormValues, onError: (message: string) => void) => {
    invalidateDnsQuery()
    setIsLoading(true)
    setIsQueryEnabled(true)

    createDomainMutation.mutate(
      {
        ...values,
        domain: normalizeDomain(values.domain),
      },
      {
        onError: (error) => {
          setIsLoading(false)
          onError(handleCreateError(values.domain, error))
        },
        onSuccess: () => {
          if (isEntriLoaded) {
            toastId.current = Toasts.notify(
              "We're pulling information about the domain. This can take up to 30 seconds.",
              {
                type: 'info',
                autoClose: false,
              },
            )
          } else {
            navigate(routing.settings.domain.configuration)
          }
        },
      },
    )
  }

  const handleSubmit = (values: FormValues, onError: (message: string) => void) => {
    if (!dnsList) {
      handleSubmitConfirm(values, onError)

      return
    }

    open(ConfirmDomainConnectModal, {
      newDomainName: values.domain,
      onConfirm: () => handleSubmitConfirm(values, onError),
    })
  }

  return (
    <NewDomainView
      onSubmit={handleSubmit}
      isLoading={createDomainMutation.isLoading || isLoading}
    />
  )
}
